/**
 * Dotted request-body paths, the one addressing scheme a command uses for its
 * fields, choices, field errors and hints: `reason`, `delivery.delivery_lines.1.quantity`.
 * Array indices are plain segments (`api-validation.md`).
 */

type Container = Record<string, unknown> | unknown[];

function segmentsOf(path: string): string[] {
  return path === "" ? [] : path.split(".");
}

export function getAt(value: unknown, path: string): unknown {
  let current: unknown = value;
  for (const segment of segmentsOf(path)) {
    if (current === null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/** A copy of `value` with `path` set; missing containers are created (an array where the next segment is an index). */
export function setAt<T>(value: T, path: string, next: unknown): T {
  const segments = segmentsOf(path);
  if (segments.length === 0) return next as T;
  const [head, ...rest] = segments;
  const container: Container =
    value !== null && typeof value === "object"
      ? Array.isArray(value)
        ? [...value]
        : { ...(value as Record<string, unknown>) }
      : /^\d+$/.test(head!)
        ? []
        : {};
  const child = (container as Record<string, unknown>)[head!];
  (container as Record<string, unknown>)[head!] =
    rest.length === 0 ? next : setAt(child, rest.join("."), next);
  return container as T;
}

/** A copy of `value` without `path` (an object key is removed; an array element is set to undefined). */
export function deleteAt<T>(value: T, path: string): T {
  const segments = segmentsOf(path);
  if (segments.length === 0 || value === null || typeof value !== "object")
    return value;
  const [head, ...rest] = segments;
  if (rest.length === 0) {
    if (Array.isArray(value)) {
      const copy = [...value];
      copy[Number(head)] = undefined;
      return copy as T;
    }
    const copy = { ...(value as Record<string, unknown>) };
    delete copy[head!];
    return copy as T;
  }
  const child = (value as Record<string, unknown>)[head!];
  if (child === undefined) return value;
  return setAt(value, head!, deleteAt(child, rest.join(".")));
}
